<template>
    <div class="pay-in__summary-card mb-3">
        <div
            class="pay-in__summary-card-header px-3 py-2 fs-small darkest"
            :class="{ 'd-flex align-items-center': isFlexHeader, 'pay-in__summary-card-header--clickable': collapsible }"
            @click="onHeaderClick"
        >
            <div :class="{ 'd-flex align-items-center': payment.reference, 'flex-grow-1': isFlexHeader }">
                <template v-if="payment.reference">
                    <reference-avatar :reference="payment.reference" :index="index" class="mr-2" />
                    <span>{{ payment.reference }}</span>
                </template>
                <template v-else>
                    Payment {{ index + 1 }}<template v-if="payment.itemsLabel"> — {{ truncateLabel(payment.itemsLabel) }}</template>
                </template>
            </div>
            <div v-if="collapsible || amount !== null" class="d-flex align-items-center flex-shrink-0 ml-2">
                <span v-if="amount !== null" class="darkest fs-small font-weight-600">{{ formatPrice(amount) }}{{ currency }}</span>
                <i
                    v-if="collapsible"
                    class="ico ml-2 dark fs-small"
                    :class="expanded ? 'ico-arrow-top' : 'ico-arrow-bottom'"
                ></i>
            </div>
        </div>

        <div v-show="!collapsible || expanded" class="pay-in__summary-card-body px-3">
            <!-- Items (partial only) -->
            <template v-if="isPartial">
                <div
                    v-for="(item, i) in payment.items"
                    :key="'item-' + i"
                    class="pay-in__summary-row"
                >
                    <span class="darkest fs-small">{{ item.label }}</span>
                    <span class="darkest fs-small">{{ formatPrice(item.total) }}€</span>
                </div>
            </template>

            <!-- Payment methods -->
            <div
                v-for="(method, mi) in payment.paymentMethods"
                :key="'method-' + mi"
                class="pay-in__summary-row"
            >
                <span class="darkest fs-small" v-if="!method.typeName">Payment method</span>
                <span class="darkest fs-small">{{ method.typeName }}</span>
                <span class="darkest fs-small">{{ formatPrice(method.amountReceived) }}€</span>
            </div>

            <!-- Change -->
            <div v-if="payment.change > 0" class="pay-in__summary-row">
                <span class="darkest fs-small">Change</span>
                <span class="darkest fs-small">{{ formatPrice(payment.change) }}€</span>
            </div>

            <!-- Invoice (create pay-in flow only) -->
            <div v-if="isFromCreatePayin" class="pay-in__summary-row">
                <span class="darkest fs-small">Invoice</span>
                <span class="darkest fs-small">{{ invoiceStatusText }}</span>
            </div>

            <!-- Billing details -->
            <div v-if="showBillingRow" class="pay-in__summary-row">
                <span class="darkest fs-small">Billing details</span>
                <span class="darkest fs-small">{{ billingDetailsText }}</span>
            </div>

            <slot></slot>
        </div>
    </div>
</template>

<script>
import ReferenceAvatar from '@/tools/modal/ReservationPayIn/ReferenceAvatar'

const MAX_LABEL_LENGTH = 50

export default {
    components: {
        ReferenceAvatar,
    },
    props: {
        payment: {
            type: Object,
            required: true,
        },
        index: {
            type: Number,
            default: 0,
        },
        isPartial: {
            type: Boolean,
            default: false,
        },
        isFromCreatePayin: {
            type: Boolean,
            default: false,
        },
        willCreateInvoiceForAllPayments: {
            type: Boolean,
            default: false,
        },
        showBillingRow: {
            type: Boolean,
            default: true,
        },
        collapsible: {
            type: Boolean,
            default: false,
        },
        defaultExpanded: {
            type: Boolean,
            default: false,
        },
        amount: {
            type: Number,
            default: null,
        },
        currency: {
            type: String,
            default: '€',
        },
    },
    data() {
        return {
            expanded: this.defaultExpanded,
        }
    },
    computed: {
        isFlexHeader() {
            return this.collapsible || this.amount !== null || !!this.payment.reference
        },
        invoiceStatusText() {
            return this.payment.createInvoice || this.willCreateInvoiceForAllPayments
                ? 'Will be created'
                : 'Will not be created'
        },
        billingDetailsText() {
            if (this.isFromCreatePayin) {
                return this.payment.hasBillingDetails
                    ? 'Will be added'
                    : 'Will not be added'
            }

            return this.payment.hasBillingDetails
                ? this.payment.billingName
                : 'Not added'
        },
    },
    methods: {
        truncateLabel(label) {
            return label.length > MAX_LABEL_LENGTH ? label.substring(0, MAX_LABEL_LENGTH) + '...' : label
        },
        onHeaderClick() {
            if (this.collapsible) this.expanded = !this.expanded
        },
    },
}
</script>

<style lang="scss" scoped>
    @import '../../../assets/scss/_variables.scss';

    .pay-in__summary-card {
        border: 1px solid $light;
        border-radius: 0.25rem;
        overflow: hidden;
        width: 100%;
        min-width: 0;
    }

    .pay-in__summary-card-header {
        background: $lighter;
        border-bottom: 1px solid $light;

        &--clickable {
            cursor: pointer;
            user-select: none;
        }
    }

    .pay-in__summary-card-body {
        padding-top: 0.25rem;
        padding-bottom: 0.25rem;
    }

    .pay-in__summary-row {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 0.25rem 0;
        border-bottom: 1px solid $lighter;

        &:last-child {
            border-bottom: none;
        }
    }

    .pay-in__green {
        color: $color-3;
    }
</style>



// WEBPACK FOOTER //
// PayInSummaryCard.vue?5849e9fa